// --------------------------------------------------------------
// Created On: 2024-03-26
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-07
// Modified By: Marco Serrano
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import PrintBom from "./PrintBom/PrintBom";
import apiRequest from "../../../utilities/api/apiRequest";
import styles from "./BomDetails.module.scss";
import ExportBom from "./ExportBom/ExportBom";

// Displays basic bom information and access to printable pdf documents.
export default function BomDetails(props: Props): Component {
  const impellerTrimText = useMemo(() => calculateImpellerTrimText(props.trim), [props.trim]);

  // Determines what the part's impeller trim should display as. If it isn't a valid value, then return 'Unknown'.
  function calculateImpellerTrimText(trim: number | null): string {
    if (trim === null || trim <= 0) {
      return "Unknown";
    } else {
      return `${trim}"`;
    }
  }

  // Checks if the file exists at the specific URL and navigates to it if it does.
  async function handleNavigateToFileUrl(fileUrl: string, notFoundErrorMessage: string) {
    const [response] = await apiRequest(fileUrl, "HEAD", null);
    if (response.ok) {
      // Attempt to open the file in a new tab.
      window.open(fileUrl, "_blank");
    } else {
      props.onError(notFoundErrorMessage);
    }
  }

  return (
    <div className="container-fluid">
      <div className="row align-items-center ">
        <div className="col-12 col-lg-5">
          <div className="container-fluid my-2">
            <div className="row justify-content-between">
              <div data-test="bom-details-pump-model" className={`col ${styles.bomDetails}`}>
                {props.pumpModel === "" ? (
                  <p className="py-3 my-0">BOM: {props.bomNumber}</p>
                ) : (
                  <p className="py-3 my-0">Pump: {props.pumpModel}</p>
                )}
              </div>
              {props.serial !== null && (
                <div data-test="bom-details-impeller-trim" className={`col ${styles.bomDetails}`}>
                  <p className="py-3 my-0">Impeller Trim: {impellerTrimText}</p>
                </div>
              )}
              {props.serial !== null && (
                <div data-test="bom-details-serial-number" className={`col ${styles.bomDetails}`}>
                  <p className="py-3 my-0">Serial Number: {props.serial}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-7">
          <div className={`container mx-0 px-1 px-xl-0 ${styles.buttonContainer}`}>
            <div className="row justify-content-between align-items-center">
              <div className="col">
                <Button
                  data-test="download-o&m-manual-btn"
                  type="button"
                  className="btn btn-primary btn-md w-100 py-3 my-0 mb-2 mb-xl-0"
                  onClick={() =>
                    handleNavigateToFileUrl(`https://${props.manualUrl}`, "O&M manual file was not found.")
                  }
                >
                  <span className="d-none d-xl-inline">Download O&M Manual</span>
                  <span className="d-none d-md-inline d-xl-none">O&M Manual</span>
                  <i className="d-inline d-md-none fa fa-2x fa-book" />
                </Button>
              </div>
              <div className="col">
                <PrintBom bomNumber={props.bomNumber} parts={props.parts} companyDiscount={props.companyDiscount} />
              </div>
              <div className="col">
                <ExportBom bomNumber={props.bomNumber} parts={props.parts} companyDiscount={props.companyDiscount} />
              </div>
              {props.pumpCurves.length > 0 && (
                <div className="col">
                  <div className="dropdown">
                    <Button
                      data-test="download-pump-curve-btn"
                      type="button"
                      className="btn btn-primary btn-md w-100 py-3 my-0 mb-2 mb-xl-0"
                      data-bs-toggle="dropdown"
                    >
                      <span className="d-none d-xl-inline">Download Pump Curve &#x25BC;</span>
                      <span className="d-none d-md-inline d-xl-none">Pump Curve &#x25BC;</span>
                      <i className="d-inline d-md-none fa fa-2x fa-line-chart" />
                    </Button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {props.pumpCurves.map((pumpCurve, index) => (
                        <li
                          key={index}
                          data-test="download-pump-curve-list-item"
                          className="text-center"
                          onClick={() =>
                            handleNavigateToFileUrl(`https://${pumpCurve.url}`, "Pump curve file was not found.")
                          }
                        >
                          <span className="dropdown-item">{pumpCurve.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  bomNumber: string;
  pumpModel: string;
  trim: number | null;
  serial: string | null;
  manualUrl: string;
  pumpCurves: PumpCurve[];
  parts: PrintBomPart[];
  companyDiscount: number | null;
  onError: (error: string) => void;
}

interface PumpCurve {
  name: string;
  url: string;
}

interface PrintBomPart {
  partNumber: string;
  description: string;
  buildQuantity: string;
  orderQuantity: string;
  paintColorId: number | null;
  paintColorName: string | null;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  units: string;
  price: number;
  weight: number;
}
